import type { EndpointDef } from './endpoint.js';
import { ApiError } from './api-error.js';
import type { z } from 'zod';

function appendToParams(params: URLSearchParams, key: string, val: unknown): void {
  if (Array.isArray(val)) {
    for (const item of val) params.append(`${key}[]`, String(item));
  } else if (val !== null && typeof val === 'object') {
    for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
      appendToParams(params, `${key}[${k}]`, v);
    }
  } else if (val !== undefined && val !== null) {
    params.set(key, String(val));
  }
}

interface CallOptions {
  baseUrl?: string;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
  /**
   * AbortSignal forwarded to the underlying `fetch()` call. Callers (e.g.
   * TanStack Query's `queryFn(context)`) can pass their own signal so an
   * in-flight request is cancelled when the query is unmounted or its key
   * changes, instead of running to completion after the caller stopped caring.
   */
  signal?: AbortSignal;
}

// eslint-disable-next-line complexity, max-lines-per-function
export async function callEndpoint<TDef extends EndpointDef>(
  def: TDef,
  request: z.input<TDef['requestSchema']>,
  options: CallOptions = {},
): Promise<z.output<TDef['responseSchema']>> {
  const { baseUrl = '', headers = {}, signal } = options;
  const credentials: RequestCredentials =
    options.credentials ?? def.defaultCredentials ?? 'same-origin';

  // Interpolate :paramName placeholders from the request object into the path.
  // Consumed path params are excluded from query string / body.
  const pathParams = new Set<string>();
  let url = `${baseUrl}${def.path}`.replace(
    /:([a-zA-Z_][a-zA-Z0-9_]*)/g,
    (_match, name: string) => {
      const value = (request as Record<string, unknown>)?.[name];
      if (value !== undefined && value !== null) {
        pathParams.add(name);
        return String(value);
      }
      return `:${name}`;
    },
  );

  // Build the remaining request fields (excluding consumed path params).
  const remaining: Record<string, unknown> = {};
  if (request != null && typeof request === 'object') {
    for (const [key, value] of Object.entries(request as Record<string, unknown>)) {
      if (!pathParams.has(key)) remaining[key] = value;
    }
  }

  // For GET requests, serialize remaining fields as URL query params.
  // Uses recursive serialization so nested objects/arrays get bracket notation.
  if (def.method === 'GET') {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(remaining)) {
      appendToParams(params, key, value);
    }
    const qs = params.toString();
    if (qs) url = `${url}?${qs}`;
  }

  const correlationId =
    headers['x-correlation-id'] ??
    (typeof crypto.randomUUID === 'function'
      ? (crypto.randomUUID() as string)
      : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2));

  const response = await fetch(url, {
    method: def.method,
    headers: {
      'Content-Type': 'application/json',
      'x-correlation-id': correlationId,
      ...headers,
    },
    credentials,
    ...(signal ? { signal } : {}),
    ...(def.method !== 'GET' && Object.keys(remaining).length > 0
      ? { body: JSON.stringify(remaining) }
      : {}),
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text().catch(() => '');
    }
    throw new ApiError(response.status, response.statusText, body);
  }

  // 204 No Content — no body to parse
  if (response.status === 204 || response.headers?.get('content-length') === '0') {
    return def.responseSchema.parse(undefined) as z.output<TDef['responseSchema']>;
  }

  const data: unknown = await response.json();
  return def.responseSchema.parse(data) as z.output<TDef['responseSchema']>;
}
